import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export interface AssetFinancials {
  id: string;
  asset_id: string;
  shop_id: string;
  purchase_date: string | null;
  purchase_cost: number | null;
  vendor: string | null;
  warranty_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkCost {
  id: string;
  shop_id: string;
  log_id: string;
  cost: number;
  created_at: string;
}

export interface SubscriptionCost {
  id: string;
  shop_id: string;
  subscription_id: string;
  cost: number;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for asset financial data - MANAGER ONLY
 * Returns null data if user is not a manager (RLS will block anyway)
 */
export function useAssetFinancials(assetId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['asset-financials', assetId],
    queryFn: async () => {
      if (!assetId || !shopId || !isManager) return null;
      
      const { data, error } = await supabase
        .from('shop_asset_financials')
        .select('*')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId)
        .maybeSingle();
      
      if (error) throw error;
      return data as AssetFinancials | null;
    },
    enabled: !!assetId && !!shopId && isManager,
  });
}

export function useUpsertAssetFinancials() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: {
      asset_id: string;
      purchase_date?: string | null;
      purchase_cost?: number | null;
      vendor?: string | null;
      warranty_expires_at?: string | null;
    }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to edit asset financials');

      // Try to update first
      const { data: existing } = await supabase
        .from('shop_asset_financials')
        .select('id')
        .eq('asset_id', input.asset_id)
        .eq('shop_id', shopId)
        .maybeSingle();
      
      if (existing) {
        const { data, error } = await supabase
          .from('shop_asset_financials')
          .update({
            purchase_date: input.purchase_date,
            purchase_cost: input.purchase_cost,
            vendor: input.vendor,
            warranty_expires_at: input.warranty_expires_at,
          })
          .eq('id', existing.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('shop_asset_financials')
          .insert({
            shop_id: shopId,
            ...input,
          })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-financials', data.asset_id] });
      toast.success('Financial data saved');
    },
    onError: (error) => {
      toast.error('Failed to save financial data', { description: error.message });
    },
  });
}

/**
 * Hook for work costs (maintenance/repair costs) - MANAGER ONLY
 */
export function useWorkCosts(assetId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['work-costs', assetId],
    queryFn: async () => {
      if (!assetId || !shopId || !isManager) return [];
      
      // Get log IDs for this asset first
      const { data: logs } = await supabase
        .from('shop_asset_maintenance_logs')
        .select('id')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId);
      
      if (!logs || logs.length === 0) return [];
      
      const logIds = logs.map(l => l.id);
      
      const { data, error } = await supabase
        .from('shop_asset_work_costs')
        .select('*')
        .eq('shop_id', shopId)
        .in('log_id', logIds);
      
      if (error) throw error;
      return data as WorkCost[];
    },
    enabled: !!assetId && !!shopId && isManager,
  });
}

export function useCreateWorkCost() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: { log_id: string; cost: number; asset_id: string }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to record asset work costs');

      const { data, error } = await supabase
        .from('shop_asset_work_costs')
        .insert({
          shop_id: shopId,
          log_id: input.log_id,
          cost: input.cost,
        })
        .select()
        .single();
      
      if (error) throw error;
      return { ...data, asset_id: input.asset_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['work-costs', data.asset_id] });
      toast.success('Cost recorded');
    },
    onError: (error) => {
      toast.error('Failed to record cost', { description: error.message });
    },
  });
}

/**
 * Hook for opportunity cost calculation - MANAGER ONLY
 */
export function useAssetOpportunityCost(assetId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['asset-opportunity-cost', assetId],
    queryFn: async () => {
      if (!assetId || !shopId || !isManager) return null;
      
      const { data, error } = await supabase.rpc('get_asset_opportunity_cost', {
        p_asset_id: assetId,
      });
      
      if (error) throw error;
      
      // Check if response contains error field
      const result = data as Record<string, unknown> | null;
      if (result?.error) return null;
      
      return result as {
        avg_daily_book_hours: number;
        current_downtime_days: number;
        estimated_lost_capacity_hours: number;
      } | null;
    },
    enabled: !!assetId && !!shopId && isManager,
  });
}
